import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, List, Paper, Grid, Chip, Divider, Tooltip } from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import { CheckCircle as CompletedIcon, MoreHoriz as InProgressIcon, Info as AssignedIcon, ForwardToInbox as ForwardIcon, Cancel as RejectedIcon } from '@mui/icons-material';
import { getSafeColor } from '../../../utils/colorUtils';

import LocalizedDate from '../../../components/common/LocalizedDate';

const AssignedToMeList = ({ tasks = [], isLoading, onNavigate }) => {
  const { t } = useTranslation(['tasks', 'common', 'documents']);

  const statusColor = (status) => {
    return getSafeColor(status);
  };

  const priorityColor = (priority) => {
    if (!priority) return 'default';
    const p = priority.toLowerCase();
    if (p === 'عاجل' || p === 'urgent') return 'error';
    if (p === 'اطمینانیه' || p === 'high') return 'warning';
    return 'default';
  };

  const statusIcon = (status) => {
    switch (status) {
      case 'ASSIGNED':
        return <AssignedIcon fontSize="small" />;
      case 'IN_PROGRESS':
      case 'ACCEPTED':
        return <InProgressIcon fontSize="small" />;
      case 'COMPLETED':
        return <CompletedIcon fontSize="small" />;
      case 'FORWARDED':
        return <ForwardIcon fontSize="small" />;
      case 'REJECTED':
        return <RejectedIcon fontSize="small" />;
      default:
        return <AssignedIcon fontSize="small" />;
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <Typography variant="body2" color="textSecondary">
          {t('common:loading', 'Loading...')}
        </Typography>
      </Box>
    );
  }

  if (!tasks.length) {
    return (
      <Typography variant="body2" color="textSecondary" align="center" sx={{ py: 4 }}>
        {t('tasks:no_assigned_tasks', 'No tasks assigned to you')}
      </Typography>
    );
  }

  return (
    <Box sx={{ p: 1, maxHeight: 300, overflowY: 'auto' }}>
      <List sx={{ padding: 0 }}>
        {tasks.map((task, index) => (
          <React.Fragment key={task.id}>
            <Paper elevation={1} sx={{ mb: 1, p: 1, cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); onNavigate(task); }}>
              <Grid container spacing={1} alignItems="center">
                {/* Title & status */}
                <Grid item xs={12} md={8}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {statusIcon(task.status)}
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {task.document?.title || t('common:unknown')}
                    </Typography>
                    {task.document?.reference_number && (
                      <Typography variant="caption" color="text.secondary">
                        {task.document.reference_number}
                      </Typography>
                    )}
                    <Chip label={t(`tasks:status_${task.status?.toLowerCase()}`, task.status)} size="small" color={statusColor(task.status)} />
                  </Box>

                  {/* Dates row */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <CalendarTodayIcon fontSize="small" color="action" />
                    <Typography variant="caption" color="text.secondary">
                      {t('tasks:assigned_on')}: <LocalizedDate date={task.assigned_at} typographyProps={{ variant: 'caption' }} />
                    </Typography>
                    {task.due_date && (
                      <>
                        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                        <CalendarTodayIcon fontSize="small" color={new Date(task.due_date) < new Date() ? 'error' : 'action'} />
                        <Typography variant="caption" color={new Date(task.due_date) < new Date() ? 'error' : 'text.secondary'}>
                          {t('tasks:due_by')}: <LocalizedDate date={task.due_date} typographyProps={{ variant: 'caption' }} />
                        </Typography>
                      </>
                    )}
                  </Box>

                  {/* Meta row */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    {(() => {
                      const pri = task.document?.priority || task.priority;
                      return (
                        <>
                          <Tooltip title={t('tasks:priority')}>
                            <PriorityHighIcon fontSize="small" color={priorityColor(pri)} />
                          </Tooltip>
                          <Typography variant="body2" color="text.secondary">
                            {pri || '-'}
                          </Typography>
                        </>
                      );
                    })()}
                    <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                    <PersonIcon fontSize="small" />
                    <Typography variant="body2" color="text.secondary">
                      {task.assigner?.full_name || task.assigner?.username || t('common:unknown')}
                    </Typography>
                  </Box>
                </Grid>

                {/* Right section removed to keep compact */}
              </Grid>
              {task.notes && (
                <Box sx={{ mt: 1, px: 1.5, py: 1, bgcolor: 'background.default', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary">"{task.notes}"</Typography>
                </Box>
              )}
            </Paper>
            {index < tasks.length - 1 && <Divider variant="inset" component="li" sx={{ my: 1 }} />}
          </React.Fragment>
        ))}
      </List>
    </Box>
  );
};

export default AssignedToMeList;
